import { QueryClient } from '@tanstack/react-query';

/**
 * Validates that a loan ID is not null, undefined, or an empty/whitespace string
 * @param loanId - The loan ID to validate
 * @returns true if the loan ID is valid (non-empty string)
 */
export const isValidLoanId = (loanId: string | null | undefined): loanId is string => {
    return !!loanId && loanId.trim() !== '';
};

/**
 * Generates the React Query key for the loan refresh endpoint
 * Used when calling loansRefreshLoanFromProvider API
 * @param loanId - The loan ID (can be either the internal ID or external loan ID)
 * @returns The query key array for React Query
 */
export const getRefreshLoanQueryKey = (loanId: string | null | undefined) =>
    ['loansRefreshLoanFromProvider', loanId] as const;

/**
 * Invalidates cached loan queries for both the internal ID and external loan ID.
 * Use after any mutation that changes loan state (authorize, send sales slip, etc.)
 * so downstream consumers refetch fresh data.
 */
export const invalidateLoanQueries = (
    queryClient: QueryClient,
    loanData:
        | { loan?: { id?: string | null; externalLoanId?: string | null } | null }
        | null
        | undefined,
) => {
    const loanId = loanData?.loan?.id;
    const externalLoanId = loanData?.loan?.externalLoanId;

    const promises: Promise<void>[] = [];

    if (loanId) {
        promises.push(queryClient.invalidateQueries({ queryKey: getRefreshLoanQueryKey(loanId) }));
    }
    if (externalLoanId) {
        promises.push(
            queryClient.invalidateQueries({ queryKey: getRefreshLoanQueryKey(externalLoanId) }),
        );
    }

    return Promise.all(promises);
};
